import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile as updateFirebaseProfile,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import {
  createUserProfile,
  getUserProfile,
  updateUserProfile,
  type UserProfile,
} from '../firebase/db';

export interface User {
  id: string;
  name: string;
  email: string;
  university?: string;
  profileImage?: string;
  accountType: 'creator' | 'buyer';
  subscriptionPlan?: 'free' | 'creator-pro' | 'elite-creator';
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; accountType?: string; error?: string }>;
  signup: (userData: Omit<User, 'id' | 'createdAt'> & { password: string }) => Promise<{ success: boolean; uid?: string; accountType?: string; error?: string }>;
  googleSignIn: () => Promise<{ success: boolean; isNewUser?: boolean; accountType?: string; error?: string }>;
  logout: () => void;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  isAuthenticated: boolean;
  // True until Firebase Auth's session restore has resolved once (either way).
  // Guards must wait for this instead of guessing with a fixed timeout —
  // restore time varies with cold starts / bundle load and a fixed delay
  // that fires too early bounces an actually-signed-in user off the page.
  authLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function profileToUser(profile: UserProfile): User {
  return {
    id: profile.id,
    name: profile.name,
    email: profile.email,
    university: profile.university,
    profileImage: profile.profileImage,
    accountType: profile.accountType,
    subscriptionPlan: profile.subscriptionPlan,
    createdAt: profile.createdAt,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const profile = await getUserProfile(firebaseUser.uid);
        if (profile) {
          setUser(profileToUser(profile));
          setIsAuthenticated(true);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const signup = async (
    userData: Omit<User, 'id' | 'createdAt'> & { password: string }
  ): Promise<{ success: boolean; uid?: string; accountType?: string; error?: string }> => {
    try {
      const credential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      const uid = credential.user.uid;

      await updateFirebaseProfile(credential.user, { displayName: userData.name });

      const profileRaw = {
        name: userData.name,
        email: userData.email,
        university: userData.university,
        profileImage: userData.profileImage,
        accountType: userData.accountType,
        createdAt: new Date().toISOString(),
      };

      // Firestore rejects undefined values — strip them out before saving
      const profile = Object.fromEntries(
        Object.entries(profileRaw).filter(([, v]) => v !== undefined)
      ) as unknown as Omit<UserProfile, 'id'>;

      await createUserProfile(uid, profile);

      setUser({ id: uid, ...profile });
      setIsAuthenticated(true);
      return { success: true, uid, accountType: userData.accountType };
    } catch (error: any) {
      console.error('Signup error code:', error.code, '| message:', error.message);
      if (error.code === 'auth/email-already-in-use') {
        return { success: false, error: 'This email is already registered. Please log in instead.' };
      }
      if (error.code === 'auth/weak-password') {
        return { success: false, error: 'Password must be at least 6 characters.' };
      }
      if (error.code === 'auth/invalid-email') {
        return { success: false, error: 'Please enter a valid email address.' };
      }
      if (error.code === 'auth/operation-not-allowed') {
        return { success: false, error: 'Email/Password sign-in is not enabled. Go to Firebase Console → Authentication → Sign-in method and enable it.' };
      }
      if (error.code === 'auth/network-request-failed') {
        return { success: false, error: 'Network error. Check your internet connection.' };
      }
      if (error.code?.startsWith('firestore/') || error.name === 'FirebaseError') {
        return { success: false, error: `Firestore error (${error.code}): Make sure Firestore Database is created in Firebase Console.` };
      }
      return { success: false, error: `Error (${error.code || 'unknown'}): ${error.message}` };
    }
  };

  const googleSignIn = async (): Promise<{ success: boolean; isNewUser?: boolean; accountType?: string; error?: string }> => {
    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(auth, provider);
      const uid = credential.user.uid;

      let profile = await getUserProfile(uid);
      const isNewUser = !profile;

      if (!profile) {
        const newProfile = {
          name: credential.user.displayName || credential.user.email?.split('@')[0] || 'User',
          email: credential.user.email!,
          profileImage: credential.user.photoURL || undefined,
          accountType: 'buyer' as const,
          createdAt: new Date().toISOString(),
        };
        const clean = Object.fromEntries(
          Object.entries(newProfile).filter(([, v]) => v !== undefined)
        ) as unknown as Omit<UserProfile, 'id'>;
        await createUserProfile(uid, clean);
        profile = { id: uid, ...clean };
      }

      setUser(profileToUser(profile));
      setIsAuthenticated(true);
      return { success: true, isNewUser, accountType: profile.accountType };
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        return { success: false, error: 'Sign-in was cancelled.' };
      }
      if (error.code === 'auth/popup-blocked') {
        return { success: false, error: 'Pop-up blocked. Please allow pop-ups for this site.' };
      }
      return { success: false, error: 'Google sign-in failed. Please try again.' };
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; accountType?: string; error?: string }> => {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;

      let profile = await getUserProfile(uid);

      // If Firestore profile is missing (e.g. it failed to save during signup),
      // create a basic one from Firebase Auth data so the user can still log in.
      if (!profile) {
        const fallback: Omit<UserProfile, 'id'> = {
          name: credential.user.displayName || email.split('@')[0],
          email: credential.user.email || email,
          accountType: 'buyer',
          createdAt: new Date().toISOString(),
        };
        await createUserProfile(uid, fallback);
        profile = { id: uid, ...fallback };
      }

      const appUser = profileToUser(profile);
      setUser(appUser);
      setIsAuthenticated(true);
      return { success: true, accountType: profile.accountType };
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        return { success: false, error: 'Incorrect email or password.' };
      }
      if (error.code === 'auth/too-many-requests') {
        return { success: false, error: 'Too many failed attempts. Please try again later.' };
      }
      return { success: false, error: 'Login failed. Please check your connection and try again.' };
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (updates: Partial<User>): Promise<void> => {
    if (!user) return;
    await updateUserProfile(user.id, updates);
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      signup,
      googleSignIn,
      logout,
      updateProfile,
      isAuthenticated,
      authLoading,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
